import { PhotoLikeBadge, WithLikeCounts } from '@/components/PhotoLikeBadge';
import { photoIdFromUrl } from '@/utils/photoLikes';
import React, { useState, useEffect } from 'react';
import { Trophy, ArrowRight } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { useTranslation } from 'react-i18next';
import { supabase } from '@/integrations/supabase/client';
import { Event } from '@/types';

interface WinnersGalleryProps {
  event: Event;
}

interface WinnerPhoto {
  id: number;
  url?: string;
}

export const WinnersGallery: React.FC<WinnersGalleryProps> = ({ event }) => {
  const { t } = useTranslation();
  const [photos, setPhotos] = useState<WinnerPhoto[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchWinnersPhotos = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('fetch-winners-photos', {
        body: { eventId: event.swiss_equestrian_id }
      });

      if (error) {
        console.error('Error fetching winners photos:', error);
        return;
      }

      if (data?.photos) {
        setPhotos(data.photos);
      } else {
        setPhotos([]);
      }
    } catch (error) {
      console.error('Error:', error);
      setPhotos([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchWinnersPhotos();
  }, [event.swiss_equestrian_id]);

  const getStrategicPhotos = (photos: WinnerPhoto[], count: number = 4) => {
    if (photos.length <= count) return photos;
    const indices: number[] = [0];
    const total = photos.length;
    if (count > 1) indices.push(Math.floor(total * 0.25));
    if (count > 2) indices.push(Math.floor(total * 0.50));
    if (count > 3) indices.push(Math.floor(total * 0.95));
    const uniqueIndices = [...new Set(indices)].sort((a, b) => a - b);
    return uniqueIndices.slice(0, count).map(i => photos[i]);
  };

  const handlePhotoClick = () => {
    window.location.href = `/${event.swiss_equestrian_id}/winners`;
  };

  if (loading) {
    return (
      <section className="mb-10">
        <div className="flex items-center space-x-2 mb-4">
          <Trophy className="w-5 h-5 text-amber-500" />
          <h3 className="font-serif italic text-xl">{t('winners.title')}</h3>
        </div>
        <div className="grid grid-cols-4 gap-3">
          {[...Array(4)].map((_, index) => (
            <Skeleton key={index} className="aspect-[4/3] rounded-sm" />
          ))}
        </div>
      </section>
    );
  }

  if (!photos.length) {
    return (
      <section className="mb-10">
        <div className="flex items-center space-x-2 mb-4">
          <Trophy className="w-5 h-5 text-amber-500" />
          <h3 className="font-serif italic text-xl">{t('winners.title')}</h3>
        </div>
        <p className="text-muted-foreground text-center py-4 text-sm">
          Foto premiazione in elaborazione...
        </p>
      </section>
    );
  }

  const previewPhotos = getStrategicPhotos(photos, 4);

  return (
    <section className="mb-10">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-2">
          <Trophy className="w-5 h-5 text-amber-500" />
          <h3 className="font-serif italic text-xl">{t('winners.title')}</h3>
        </div>
        <button
          onClick={handlePhotoClick}
          className="flex items-center gap-1.5 text-xs uppercase tracking-[0.15em] text-primary hover:text-primary/80 transition-colors font-medium"
        >
          {t('winners.galleryLink')}
          <ArrowRight className="w-3.5 h-3.5" />
        </button>
      </div>

      <WithLikeCounts eventId={event.swiss_equestrian_id} photoIds={previewPhotos.map(p => p.id)}>{counts => (
      <div className="grid grid-cols-4 gap-3">
        {previewPhotos.map((photo, index) => (
          <div
            key={photo.id}
            className="relative aspect-[4/3] cursor-pointer overflow-hidden rounded-sm hover:opacity-80 transition-opacity active:scale-[0.97]"
            onClick={handlePhotoClick}
          >
            <img
              src={`https://api.equiphoto.ch/thumbnails/${event.swiss_equestrian_id}/winners/${photo.id}`}
              alt={`${t('winners.title')} ${index + 1}`}
              className="w-full h-full object-cover"
              loading="lazy"
            />
            <PhotoLikeBadge count={counts[photo.id]} />
          </div>
        ))}
      </div>
      )}</WithLikeCounts>
    </section>
  );
};
